import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { emailTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import { normalizeVariable, formatVariableForPrompt } from '../utils/templateVariableHelpers'
import {
  LoadingSpinner,
  PageHeader,
  Button,
  FormField,
  FormSelect,
  FormTextarea,
  Card,
  CardHeader,
  CardBody,
  ErrorMessage,
  Modal
} from '../components/common'

const EditEmailTemplate = () => {
  const { templateType } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const preselectedType = !templateType ? searchParams.get('type') : null
  const [formData, setFormData] = useState({
    templateType: 0,
    subject: '',
    htmlBody: '',
    textBody: ''
  })
  const [variables, setVariables] = useState([])
  const [templateTypes, setTemplateTypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useState('split') // 'code', 'preview', 'split'
  const [showVariables, setShowVariables] = useState(true)
  const [initialLoading, setInitialLoading] = useState(true)
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [showReviseModal, setShowReviseModal] = useState(false)
  const [revisePrompt, setRevisePrompt] = useState('')
  const [reviseLoading, setReviseLoading] = useState(false)
  const [reviseError, setReviseError] = useState(null)
  const [aiGenerateNew, setAiGenerateNew] = useState(false)

  useEffect(() => {
    loadTemplateTypes()
    if (templateType) {
      loadTemplateDetails()
    } else if (preselectedType) {
      const typeVal = parseInt(preselectedType)
      setFormData(prev => ({ ...prev, templateType: typeVal }))
      loadTemplateVariables(typeVal)
      setInitialLoading(false)
    } else {
      setInitialLoading(false)
    }
  }, [templateType])

  useEffect(() => {
    if (!aiPrompt && formData.templateType && variables.length > 0 && templateTypes.length > 0) {
      const typeName = getTemplateTypeName(formData.templateType)
      const varList = variables.map(formatVariableForPrompt).join(', ')
      setAiPrompt(
        `Generate a professional ${typeName} email template for a building management platform. Available variables: ${varList}`
      )
    }
  }, [formData.templateType, variables, templateTypes])

  const loadTemplateTypes = async () => {
    try {
      const response = await emailTemplatesApi.getTypes()
      setTemplateTypes(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error('Failed to load template types:', err)
      setTemplateTypes([])
    }
  }

  const loadTemplateDetails = async () => {
    try {
      setInitialLoading(true)
      const response = await emailTemplatesApi.get(templateType)
      const templateData = {
        templateType: parseInt(templateType),
        subject: response.data.subjectPart || response.data.subject || '',
        htmlBody: response.data.htmlPart || response.data.htmlBody || '',
        textBody: response.data.textPart || response.data.textBody || ''
      }
      setFormData(templateData)
      loadTemplateVariables(parseInt(templateType))
    } catch (err) {
      console.error('Failed to load template details:', err)
      setError(extractErrorMessage(err))
    } finally {
      setInitialLoading(false)
    }
  }

  const loadTemplateVariables = async (templateTypeValue) => {
    try {
      console.log('Loading variables for template type:', templateTypeValue)
      const response = await emailTemplatesApi.getVariablesByType(templateTypeValue)
      console.log('Variables API response:', response)
      console.log('Variables data:', response.data)
      
      // The API returns {templateType: 0, variables: [{name, isRequired, description}, ...]}
      // We store the full objects to have access to all properties
      if (response.data && Array.isArray(response.data.variables)) {
        setVariables(response.data.variables)
      } else if (Array.isArray(response.data)) {
        setVariables(response.data)
      } else {
        setVariables([])
      }
    } catch (err) {
      console.error('Failed to load template variables:', err)
      console.error('Error response:', err.response)
      setVariables([])
    }
  }

  const getTemplateTypeName = (value) => {
    const type = templateTypes.find(t => t.value === parseInt(value))
    return type ? type.name : `Type ${value}`
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'templateType' ? parseInt(value) : value
    }))
  }

  const handleHtmlBodyChange = (value) => {
    setFormData(prev => ({
      ...prev,
      htmlBody: value || ''
    }))
  }

  const handleTemplateTypeChange = (e) => {
    const newType = parseInt(e.target.value)
    setFormData(prev => ({
      ...prev,
      templateType: newType
    }))
    loadTemplateVariables(newType)
  }

  const insertVariable = (variable) => {
    // If variable is an object, use its name property
    const variableName = typeof variable === 'object' ? variable.name : variable
    const placeholder = `{{${variableName}}}`
    setFormData(prev => ({
      ...prev,
      htmlBody: prev.htmlBody + placeholder
    }))
    
    if (viewMode !== 'code') {
      setViewMode('split')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const apiData = {
        templateType: formData.templateType,
        subject: formData.subject,
        htmlBody: formData.htmlBody,
        textBody: formData.textBody
      }

      if (templateType) {
        await emailTemplatesApi.update(apiData)
      } else {
        await emailTemplatesApi.create(apiData)
      }

      navigate('/email-templates')
    } catch (err) {
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Please enter a description for the AI to generate content')
      return
    }

    setAiLoading(true)
    setAiError(null)

    try {
      const aiRequest = {
        prompt: aiPrompt,
        templateType: formData.templateType,
        ...(aiGenerateNew
          ? { generateNew: true }
          : {
              currentTitle: formData.subject || undefined,
              currentHtmlBody: formData.htmlBody || undefined,
              currentTextBody: formData.textBody || undefined
            }
        )
      }

      const response = await emailTemplatesApi.generateAI(aiRequest)
      
      // Update form with AI-generated content
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          subject: response.data.subject || response.data.title || prev.subject,
          htmlBody: response.data.htmlBody || response.data.htmlPart || prev.htmlBody,
          textBody: response.data.textBody || response.data.textPart || prev.textBody
        }))
        setShowAIModal(false)
        setAiPrompt('')
      }
    } catch (err) {
      setAiError(extractErrorMessage(err))
    } finally {
      setAiLoading(false)
    }
  }

  const handleAIRevise = async () => {
    if (!revisePrompt.trim()) {
      setReviseError('Please enter revision instructions')
      return
    }

    setReviseLoading(true)
    setReviseError(null)

    try {
      const response = await emailTemplatesApi.reviseAI({
        templateType: formData.templateType,
        currentTitle: formData.subject,
        currentHtmlBody: formData.htmlBody,
        currentTextBody: formData.textBody,
        revisionPrompt: revisePrompt
      })

      if (response.data) {
        setFormData(prev => ({
          ...prev,
          subject: response.data.subject || response.data.title || prev.subject,
          htmlBody: response.data.htmlBody || prev.htmlBody,
          textBody: response.data.textBody || prev.textBody
        }))
        setShowReviseModal(false)
        setRevisePrompt('')
      }
    } catch (err) {
      setReviseError(extractErrorMessage(err))
    } finally {
      setReviseLoading(false)
    }
  }

  const renderReviseModal = () => {
    if (!showReviseModal) return null

    return (
      <Modal
        isOpen={showReviseModal}
        onClose={() => setShowReviseModal(false)}
        title="Revise Template with AI"
        size="large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {reviseError && (
            <ErrorMessage
              message={reviseError}
              onDismiss={() => setReviseError(null)}
            />
          )}

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              The AI will revise your existing template based on your instructions while preserving template variables.
            </p>
          </div>

          <FormTextarea
            label="What changes would you like?"
            name="revisePrompt"
            value={revisePrompt}
            onChange={(e) => setRevisePrompt(e.target.value)}
            rows={4}
            placeholder="E.g., Make the tone more friendly, add a call-to-action button, improve the layout..."
            disabled={reviseLoading}
          />

          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowReviseModal(false)
                setRevisePrompt('')
                setReviseError(null)
              }}
              disabled={reviseLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleAIRevise()
              }}
              disabled={reviseLoading || !revisePrompt.trim()}
              loading={reviseLoading}
            >
              Revise
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const renderAIModal = () => {
    if (!showAIModal) return null

    return (
      <Modal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        title="Generate Template with AI"
        size="large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {aiError && (
            <ErrorMessage 
              message={aiError}
              onDismiss={() => setAiError(null)}
            />
          )}
          
          <FormTextarea
            label="Describe the email template you want"
            name="aiPrompt"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={4}
            placeholder="E.g., Create a welcome email for new building residents that includes their unit number, building name, and instructions for accessing amenities..."
            disabled={aiLoading}
          />
          
          {formData.templateType && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Template Type:</strong> {getTemplateTypeName(formData.templateType)}
              </p>
              {(formData.subject || formData.htmlBody) && !aiGenerateNew && (
                <p className="text-xs text-blue-600 mt-1">
                  The AI will improve upon your existing content.
                </p>
              )}
            </div>
          )}

          {(formData.subject || formData.htmlBody) && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={aiGenerateNew}
                onChange={(e) => setAiGenerateNew(e.target.checked)}
                disabled={aiLoading}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Generate from scratch (ignore existing content)</span>
            </label>
          )}
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAIModal(false)
                setAiPrompt('')
                setAiError(null)
                setAiGenerateNew(false)
              }}
              disabled={aiLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAIGenerate();
              }}
              disabled={aiLoading || !aiPrompt.trim()}
              loading={aiLoading}
            >
              Generate
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  const renderHtmlPreview = () => {
    return (
      <div className="h-full border border-gray-300 rounded-md overflow-hidden">
        <iframe
          srcDoc={formData.htmlBody}
          className="w-full h-full"
          title="HTML Preview"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    )
  }

  if (initialLoading) {
    return <LoadingSpinner />
  }

  // Header actions will be added to the form

  return (
    <div className="flex flex-col h-full space-y-6">
      <PageHeader 
        title={templateType ? `Edit ${getTemplateTypeName(templateType)}` : 'Create New Template'}
        subtitle="Design and customize your email template"
        backLink={{
          label: "Back to Templates",
          href: "/email-templates"
        }}
      />

      {error && (
        <ErrorMessage 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex gap-6">
          {/* Main Form */}
          <Card className="flex-1 overflow-y-auto">
            <CardBody>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <FormSelect
                    label="Template Type"
                    name="templateType"
                    value={formData.templateType}
                    onChange={handleTemplateTypeChange}
                    options={[
                      { value: '', label: 'Select a template type' },
                      ...templateTypes.map(type => ({ value: type.value, label: type.name }))
                    ]}
                    required
                    disabled={!!templateType || !!preselectedType}
                  />

                  <FormField
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  />
                </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    HTML Body
                  </label>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="primary"
                      size="small"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowAIModal(true);
                      }}
                      disabled={formData.templateType === null || formData.templateType === undefined || formData.templateType === ''}
                      title={(formData.templateType === null || formData.templateType === undefined || formData.templateType === '') ? "Select a template type first" : "Generate content with AI"}
                      className="bg-purple-600 hover:bg-purple-700 text-white border-0"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Generate
                    </Button>
                    {templateType && formData.htmlBody && (
                      <Button
                        variant="primary"
                        size="small"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowReviseModal(true);
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white border-0"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        AI Revise
                      </Button>
                    )}
                    <div className="border-l border-gray-300 h-6" />
                    <Button
                      variant={viewMode === 'code' ? 'primary' : 'secondary'}
                      size="small"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMode('code');
                      }}
                    >
                      Code
                    </Button>
                    <Button
                      variant={viewMode === 'split' ? 'primary' : 'secondary'}
                      size="small"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMode('split');
                      }}
                    >
                      Split
                    </Button>
                    <Button
                      variant={viewMode === 'preview' ? 'primary' : 'secondary'}
                      size="small"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setViewMode('preview');
                      }}
                    >
                      Preview
                    </Button>
                  </div>
                </div>
                
                <div className="border border-gray-300 rounded-md" style={{ height: '500px' }}>
                  {viewMode === 'code' && (
                    <Editor
                      height="500px"
                      language="html"
                      value={formData.htmlBody}
                      onChange={handleHtmlBodyChange}
                      theme="vs-light"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        wordWrap: 'on',
                        formatOnPaste: true,
                        formatOnType: true
                      }}
                    />
                  )}
                  
                  {viewMode === 'split' && (
                    <div className="flex h-full">
                      <div className="w-1/2 border-r border-gray-300">
                        <Editor
                          height="500px"
                          language="html"
                          value={formData.htmlBody}
                          onChange={handleHtmlBodyChange}
                          theme="vs-light"
                          options={{
                            minimap: { enabled: false },
                            fontSize: 12,
                            wordWrap: 'on',
                            formatOnPaste: true,
                            formatOnType: true
                          }}
                        />
                      </div>
                      <div className="w-1/2">
                        {renderHtmlPreview()}
                      </div>
                    </div>
                  )}
                  
                  {viewMode === 'preview' && renderHtmlPreview()}
                </div>
                
                <div className="text-xs text-gray-500 mt-1">
                  Content length: {formData.htmlBody?.length || 0} characters
                </div>
              </div>

                <FormTextarea
                  label="Text Body"
                  name="textBody"
                  value={formData.textBody}
                  onChange={handleInputChange}
                  rows={6}
                  required
                />

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/email-templates');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={loading}
                  >
                    Save Template
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Variables Sidebar */}
          <Card className={`transition-all duration-300 ${showVariables ? 'w-80' : 'w-12'}`}>
            <div className="h-full flex flex-col">
              <CardHeader>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowVariables(!showVariables);
                  }}
                  className="w-full justify-between"
                >
                  {showVariables ? (
                    <>
                      <span className="text-lg font-semibold">Template Variables</span>
                      <span className="text-gray-400">›</span>
                    </>
                  ) : (
                    <span className="text-gray-400 mx-auto">‹</span>
                  )}
                </Button>
              </CardHeader>
              
              {showVariables && (
                <CardBody className="flex-1 overflow-y-auto">
                  <p className="text-xs text-gray-600 mb-4">
                    Type: {getTemplateTypeName(formData.templateType)}
                  </p>
                  
                  {variables.length === 0 ? (
                    <div>
                      <p className="text-gray-500 text-sm mb-2">No variables available for this template type.</p>
                      <p className="text-xs text-gray-400 mt-2">Some template types may not have predefined variables.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-3">
                        Click on a variable to insert it into the HTML body:
                      </p>
                      {variables.some(v => v.isRequired) && (
                        <p className="text-xs text-red-500 mb-2">* Required variables</p>
                      )}
                      {variables.map((variable, index) => {
                        const { name: variableName, isRequired, description, type: variableType } = normalizeVariable(variable)

                        return (
                          <div key={index} className="mb-2">
                            <Button
                              variant="ghost"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                insertVariable(variable);
                              }}
                              className="w-full justify-start p-2 bg-gray-50 border border-gray-200 rounded-md hover:bg-primary-50 hover:border-primary-300 text-sm font-mono break-all"
                            >
                              <div className="flex items-center justify-between w-full">
                                <span>{`{{${variableName}}}`}</span>
                                <span className="flex items-center">
                                  {isRequired && (
                                    <span className="text-xs text-red-500 ml-2">*</span>
                                  )}
                                  {variableType && variableType !== 'Text' && (
                                    <span className="text-[10px] text-blue-500 ml-1 font-normal">{variableType.toLowerCase()}</span>
                                  )}
                                </span>
                              </div>
                            </Button>
                            {description && (
                              <p className="text-xs text-gray-500 mt-1 ml-2">{description}</p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">HTML Editor Tips:</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• <strong>Code:</strong> Edit HTML source</li>
                      <li>• <strong>Split:</strong> Edit and preview side-by-side</li>
                      <li>• <strong>Preview:</strong> See rendered output</li>
                      <li>• Click variables to insert at end</li>
                    </ul>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-2">Variable Usage:</h4>
                    <ul className="text-xs text-yellow-700 space-y-1">
                      <li>• Variables are case-sensitive</li>
                      <li>• Use double curly braces: {`{{variable}}`}</li>
                      <li>• Required variables must be included</li>
                      <li>• Variables will be replaced when email is sent</li>
                    </ul>
                  </div>

                  {/* Debug info - check browser console */}
                  <details className="mt-4">
                    <summary className="text-xs text-gray-500 cursor-pointer">Debug Info</summary>
                    <div className="text-xs bg-gray-100 p-2 mt-2 rounded overflow-auto">
                      <p>Template Type: {formData.templateType}</p>
                      <p>Variables Count: {variables.length}</p>
                      <Button
                        variant="ghost"
                        size="small"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          loadTemplateVariables(formData.templateType);
                        }}
                        className="text-primary-600 text-xs underline mt-2"
                      >
                        Retry loading variables
                      </Button>
                    </div>
                  </details>
                </CardBody>
              )}
            </div>
          </Card>
        </div>
      </div>
      
      {renderAIModal()}
      {renderReviseModal()}
    </div>
  )
}

export default EditEmailTemplate